import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Terminal } from "lucide-react";
import { skills, skillCategories, codeSnippet } from "@/data/portfolio";

export const Skills = () => {
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? skills : skills.filter((s) => s.category === filter);

  return (
    <section id="skills" className="relative py-24 lg:py-36" data-testid="skills-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[600px] -translate-x-1/2 rounded-full orb-violet" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 03 — Arsenal"}
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Skills, measured<br />in <span className="gradient-text">production</span>
          </h2>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="skills-filter-tabs">
          {skillCategories.map((cat) => (
            <button
              key={cat}
              data-testid={`skills-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setFilter(cat)}
              className={`rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-all duration-300 ${
                filter === cat
                  ? "bg-cyan-500/15 text-cyan-300 border border-cyan-400/50 glow-cyan"
                  : "border border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-7">
            <AnimatePresence mode="popLayout">
              {visible.map((s) => (
                <motion.div
                  layout
                  key={s.name}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="card-glass spotlight-card group rounded-xl border border-white/10 p-5 transition-all duration-300 hover:border-violet-500/40 hover:glow-violet"
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
                    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
                  }}
                  data-testid={`skill-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-display text-base font-semibold text-slate-100">{s.name}</h3>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {s.years}y
                    </span>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <span className="h-px w-6 bg-gradient-to-r from-cyan-400 to-violet-500 transition-all duration-500 group-hover:w-12" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {s.category}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <div
              className="overflow-hidden rounded-xl border border-white/10 bg-[#0A0B12]"
              data-testid="skills-code-snippet"
            >
              <div className="flex items-center gap-2 border-b border-white/10 px-5 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <span className="ml-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <Terminal size={12} /> black_scholes.py
                </span>
              </div>
              <pre className="overflow-x-auto p-5 font-mono text-xs leading-relaxed text-slate-300">
                <code>{codeSnippet}</code>
              </pre>
            </div>
            <p className="mt-4 font-mono text-[11px] leading-relaxed text-slate-500">
              $ python black_scholes.py — because pricing theory deserves production-grade code.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
